import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import type {
    NavigationScreenSiteIntegrationsModalState,
    NavigationScreenSiteState,
    NavigationScreenSpaceIntegrationsModalState,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Dialog,
    DialogBody,
    DialogRoot,
    Divider,
    LoadingPanel,
    Sidebar,
    SidebarBody,
    SidebarItemList,
    SidebarLink,
    SidebarSection,
    SidebarSectionHeading,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import {
    APIErrorBoundary,
    FeaturedIntegrations,
    INTEGRATIONS_CATEGORY_PROPS,
    INTEGRATIONS_PER_PAGE,
    useFeaturedIntegrations,
} from '../../Components';
import {
    NavigationRedirect,
    useHistory,
    useNavigation,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery } from '../../state';
import {
    IntegrationIntro,
    IntegrationOverview,
    IntegrationsEmptyState,
    IntegrationsList,
} from './components';
import { useIntegrationsModalSiteScreen, useIntegrationsModalSpaceScreen } from './hooks';
import { Integrations } from './IntegrationsScreen';

/**
 * Modal to browse and manage integrations in a space or site.
 */
export function IntegrationsModal(props: {
    screen: NavigationScreenSiteState | NavigationScreenSpaceState;
}): React.ReactElement | null {
    const { screen } = props;
    const { navigate } = useNavigation();

    if (!screen.sideSheet || screen.sideSheet.type !== 'integrations') {
        return null;
    }

    return (
        <DialogRoot
            isOpen={screen.sideSheet.type === 'integrations'}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    navigate({ current: { sideSheet: undefined } });
                }
            }}
        >
            {(() => {
                switch (screen.type) {
                    case 'site':
                        return <SiteIntegrationsModal />;
                    case 'space':
                        return <SpaceIntegrationsModal />;
                    default:
                        return <IntegrationsEmptyState kind="error" />;
                }
            })()}
        </DialogRoot>
    );
}

/**
 * Integrations modal for a site.
 */
function SiteIntegrationsModal(): React.ReactElement {
    const screen = useIntegrationsModalSiteScreen();
    return <IntegrationsModalWithScreen screen={screen} />;
}

/**
 * Integrations modal for a space.
 */
function SpaceIntegrationsModal(): React.ReactElement {
    const screen = useIntegrationsModalSpaceScreen();
    return <IntegrationsModalWithScreen screen={screen} />;
}

function IntegrationsModalWithScreen(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement | null {
    const { screen } = props;

    return (
        <Dialog size={{ width: 1168, height: 640 }}>
            <div className="flex size-full">
                <IntegrationsModalSidebar screen={screen} />
                <IntegrationsModalMain screen={screen} />
            </div>
        </Dialog>
    );
}

function IntegrationsModalSidebar(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement {
    const { screen } = props;
    const { sideSheet } = screen;

    return (
        <Sidebar className="hidden shrink-0 md:block md:pt-6">
            <SidebarBody>
                <SidebarSection>
                    <SidebarSectionHeading>In this {screen.type}</SidebarSectionHeading>
                    <SidebarItemList>
                        <SidebarLink
                            label="Installed integrations"
                            isActive={sideSheet.view?.type === 'installations'}
                            href={{
                                current: {
                                    sideSheet: {
                                        ...sideSheet,
                                        view: { type: 'installations' },
                                    },
                                },
                            }}
                        />
                    </SidebarItemList>
                </SidebarSection>
                <SidebarSection>
                    <SidebarSectionHeading>Categories</SidebarSectionHeading>
                    <SidebarItemList>
                        <SidebarLink
                            key="all"
                            label="All"
                            isActive={
                                sideSheet.view?.type === 'category' &&
                                sideSheet.view.category === undefined
                            }
                            href={{
                                current: {
                                    sideSheet: {
                                        ...sideSheet,
                                        view: { type: 'category', category: undefined },
                                    },
                                },
                            }}
                        />
                        <React.Suspense fallback={null}>
                            <IntegrationsCategoriesSidebarLinks screen={screen} />
                        </React.Suspense>
                    </SidebarItemList>
                </SidebarSection>
            </SidebarBody>
        </Sidebar>
    );
}

/**
 * List categories in the modal, which requires an API request.
 */
function IntegrationsCategoriesSidebarLinks(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}) {
    const { screen } = props;
    const { sideSheet, organization } = screen;
    assertIsDefined(organization);

    // Note: Because we want to maintain the loading state on the integrations list, not on the whole modal,
    // we can't fetch the integrations higher in the tree to get the correct categories list here as prop.
    // We may want to create a `getIntegrationsCategories` endpoint to avoid having to fetch all integrations.
    const { data } = useSuspenseQuery(
        apiQuery(
            'listOrganizationIntegrations',
            { organizationId: organization, limit: INTEGRATIONS_PER_PAGE },
            { nullOnError: true }
        )
    );
    // Refactor: handle the error case in a react boundary
    const integrations = data?.items || [];

    const targetIntegrations = integrations.filter(
        (integration) =>
            integration.target === screen.type || integration.target === api.IntegrationTarget.All
    );
    const categories: api.IntegrationCategory[] = [];
    for (const integration of targetIntegrations) {
        if (!categories.includes(integration.categories[0])) {
            categories.push(integration.categories[0]);
        }
    }

    return categories.map((categoryKey) => (
        <SidebarLink
            key={categoryKey}
            label={INTEGRATIONS_CATEGORY_PROPS[categoryKey].label}
            isActive={
                sideSheet.view?.type === 'category' && sideSheet.view.category === categoryKey
            }
            href={{
                current: {
                    sideSheet: {
                        ...sideSheet,
                        view: { type: 'category', category: categoryKey },
                    },
                },
            }}
        />
    ));
}

function IntegrationsModalMain(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement {
    const { screen } = props;
    const { sideSheet, organization } = screen;
    assertIsDefined(organization);

    const analyticsScreen = (() => {
        const analyticsContext = {
            organization,
            siteId: screen.type === 'site' ? screen.site : undefined,
            spaceId: screen.type === 'space' ? screen.space : undefined,
        };

        if (sideSheet.view && sideSheet.view.type === 'integration') {
            return {
                ...analyticsContext,
                screen: screen.type === 'site' ? 'site_integration' : 'space_integration',
                integration: sideSheet.view.integration,
            } as const;
        }

        if (sideSheet.view) {
            return {
                ...analyticsContext,
                screen: screen.type === 'site' ? 'site_integrations' : 'space_integrations',
                screenTab: sideSheet.view.type,
                category:
                    'category' in sideSheet.view && sideSheet.view.category
                        ? sideSheet.view.category
                        : undefined,
            } as const;
        }

        return {
            ...analyticsContext,
            screen: screen.type === 'site' ? 'site_integrations' : 'space_integrations',
            screenTab: 'installations',
        } as const;
    })();

    useSetAnalyticsScreen(analyticsScreen, [screen.type, sideSheet.view]);

    if (!sideSheet.view) {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        sideSheet: {
                            type: 'integrations',
                            view: { type: 'installations' },
                        },
                    },
                }}
            />
        );
    }

    return (
        <div className="flex size-full flex-1 flex-col md:overflow-auto">
            <DialogBody data-testid="integrations.modal" className="size-full">
                <ErrorBoundary
                    fallback={<IntegrationsEmptyState kind="error" className="h-full" />}
                >
                    <APIErrorBoundary
                        fallback={{
                            404: <IntegrationsEmptyState kind="error" className="h-full" />,
                            403: <IntegrationsEmptyState kind="error" className="h-full" />,
                        }}
                    >
                        <React.Suspense fallback={<LoadingPanel className="my-auto h-full" />}>
                            {(() => {
                                switch (sideSheet.view?.type) {
                                    case 'installations':
                                    case undefined:
                                        return <InstalledIntegrations screen={screen} />;

                                    case 'category':
                                        return <TargetIntegrations screen={screen} />;

                                    case 'integration':
                                        return (
                                            <IntegrationView
                                                integrationName={sideSheet.view.integration}
                                                screen={screen}
                                            />
                                        );

                                    default:
                                        assertNever(sideSheet.view);
                                }
                            })()}
                        </React.Suspense>
                    </APIErrorBoundary>
                </ErrorBoundary>
            </DialogBody>
        </div>
    );
}

function InstalledIntegrations(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement | null {
    const { screen } = props;
    const { organization } = screen;
    assertIsDefined(organization);

    const [{ data: integrations }, { data: installedIntegrations }] = useSuspenseQueries({
        queries: [
            apiQuery('listOrganizationIntegrations', {
                organizationId: organization,
                limit: INTEGRATIONS_PER_PAGE,
                target:
                    screen.type === 'space'
                        ? api.IntegrationTarget.Space
                        : api.IntegrationTarget.Site,
            }),
            apiQuery('listOrganizationIntegrationsStatus', {
                organizationId: organization,
                target: screen.type === 'space' ? 'space' : 'site',
                limit: 100,
            }),
        ],
    });

    if (screen.sideSheet.view?.type !== 'installations') {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        sideSheet: {
                            type: 'integrations',
                            view: { type: 'installations' },
                        },
                    },
                }}
            />
        );
    }

    assertIsDefined(installedIntegrations);

    return (
        <div className="flex h-min flex-col gap-10">
            <div className="flex h-full flex-col gap-4">
                <h2 className="text-ui-heading-large">Installed integrations</h2>

                {installedIntegrations.items.length === 0 ? (
                    <div className="size-full rounded-lg border">
                        <IntegrationsEmptyState kind="no-installations" />
                    </div>
                ) : (
                    <IntegrationsList
                        organizationId={organization}
                        integrations={integrations.items}
                        installedIntegrations={installedIntegrations.items}
                    />
                )}
            </div>

            <FeaturedIntegrationsForContent
                integrations={integrations.items}
                installedIntegrations={installedIntegrations.items}
                screen={screen}
            />
        </div>
    );
}

/** List of featured integrations for a space or site */
function FeaturedIntegrationsForContent(props: {
    integrations: api.Integration[];
    installedIntegrations: api.InstalledIntegration[];
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement | null {
    const { integrations, installedIntegrations, screen } = props;
    const { organization } = screen;
    assertIsDefined(organization);

    const featuredIntegrations = useFeaturedIntegrations({
        integrations,
        installedIntegrations: installedIntegrations.map((installed) => installed.integration.name),
        useCase:
            screen.type === 'site'
                ? api.OrganizationUseCase.DocsSite
                : api.OrganizationUseCase.InternalDocs,
        limit: 6,
    });

    if (featuredIntegrations.length === 0) {
        return null;
    }

    return (
        <>
            <Divider />

            <FeaturedIntegrations
                integrations={featuredIntegrations}
                title={<h2 className="text-ui-heading-large">Recommended for you</h2>}
                organizationId={organization}
                className="grid grid-cols-[repeat(auto-fill,_minmax(16rem,_1fr))] gap-6 lg:grid-cols-3"
            />
        </>
    );
}

function TargetIntegrations(props: {
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement {
    const { screen } = props;
    const { sideSheet, organization } = screen;
    assertIsDefined(organization);

    const { data: installedIntegrations } = useSuspenseQuery(
        apiQuery('listOrganizationIntegrationsStatus', {
            organizationId: organization,
            target: screen.type === 'space' ? 'space' : 'site',
            limit: 100,
        })
    );

    if (sideSheet.view?.type !== 'category') {
        return (
            <NavigationRedirect
                to={{ current: { sideSheet: { view: { type: 'category', category: undefined } } } }}
            />
        );
    }

    assertIsDefined(installedIntegrations);

    const { category, provider, search } = sideSheet.view;

    return (
        <Integrations
            target={
                screen.type === 'space' ? api.IntegrationTarget.Space : api.IntegrationTarget.Site
            }
            search={search}
            category={category}
            provider={provider}
            organization={organization}
            installedIntegrations={installedIntegrations.items}
        />
    );
}

function IntegrationView(props: {
    integrationName: string;
    screen:
        | NavigationScreenSiteIntegrationsModalState
        | NavigationScreenSpaceIntegrationsModalState;
}): React.ReactElement {
    const { integrationName, screen } = props;
    const history = useHistory();

    const { data: integration } = useSuspenseQuery(
        apiQuery('getIntegrationByName', { integrationName })
    );

    return (
        <div className="flex flex-col gap-6">
            <Button
                label="Back"
                kind="secondary"
                leadingIcon={Icon.ArrowLeft}
                className="self-start"
                onPress={() => {
                    history.go(-1);
                }}
            />

            <div className="flex flex-col gap-10">
                <IntegrationIntro integration={integration} screen={screen} />
                <Divider />
                <IntegrationOverview integration={integration} />
            </div>
        </div>
    );
}
